import { Button, Card, Col, Layout, Row, Tabs, Tag } from 'antd';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import {
	getStripeAccount,
	getStripeAccountLink,
} from 'redux/reducers/financeSlice';
import { AppDispatch } from 'redux/store';
import styles from './FinancePage.module.less';
import {
	BankFilled,
	CheckCircleOutlined,
	GoldFilled,
	CreditCardOutlined,
} from '@ant-design/icons';
import SalesDetails from '../SalesDetails';
import RevenueAnalytics from '../RevenueAnalytics';
import Payments from '../Payments/Payments';
import { ProjectProductDetailsDto } from 'api';
import { getProjectProduct } from 'redux/reducers/projectsSlice';
import OmniNotification from 'components/OmniNotification';
import { mapErrorToErrorMessage } from 'utils/errorUtils';
const { TabPane } = Tabs;

type ParamsProps = {
	projectId: string;
};
interface StripeDetails {
	last4: string;
	routingNumber: number;
	currency: string;
	bankName: string;
	accountLink?: string;
	brand: string;
}
export default function FinancePage() {
	const { t } = useTranslation();
	const history = useHistory();
	const [fetchingAccountLink, setFetchingAccountLink] = useState(false);
	const { projectId } = useParams<ParamsProps>();
	const dispatch: AppDispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(true);
	const [stripeAccountInfo, setStripeAccountInfo] = useState<StripeDetails>();
	const [productDetails, setProductDetails] =
		useState<ProjectProductDetailsDto>();
	const fetchAndRedirectToStripe = () => {
		setFetchingAccountLink(true);

		dispatch(getStripeAccountLink(parseInt(projectId))).then((result) => {
			setFetchingAccountLink(false);
			if (getStripeAccountLink.fulfilled.match(result)) {
				redirectToStripe(result.payload.url);
			} else {
				OmniNotification.error({
					message: t('notification.error.title'),
					description:
						mapErrorToErrorMessage(result.error) || t('api.defaultError'),
				});
			}
		});
	};
	const redirectToStripe = (url?: string) => {
		if (url) {
			//@ts-ignore
			window.location = url;
		}
	};
	useEffect(() => {
		dispatch(getProjectProduct(parseInt(projectId))).then((result) => {
			if (result.payload) {
				setProductDetails(result.payload);
			}
		});
		dispatch(getStripeAccount(parseInt(projectId))).then((result) => {
			if (getStripeAccount.fulfilled.match(result)) {
				if (!result?.payload?.stripeResponse) {
					setIsLoading(false);
					return;
				}
				const accountInfo = JSON.parse(result?.payload?.stripeResponse);
				const externalAccountData = accountInfo.external_accounts?.data[0];
				const stripeDetails = {
					last4: externalAccountData?.last4,
					routingNumber: externalAccountData?.routing_number,
					currency: accountInfo.default_currency,
					bankName: externalAccountData?.bank_name,
					accountLink: result?.payload?.metadata?.accountLink,
					brand: externalAccountData.brand,
				};
				setStripeAccountInfo(stripeDetails);
			} else {
				OmniNotification.error({
					message: t('notification.error.title'),
					description:
						mapErrorToErrorMessage(result.error) || t('api.defaultError'),
				});
			}
			setIsLoading(false);
		});
	}, []);

	if (isLoading) {
		return <></>;
	}
	const getAccountDetailsCard = (stripeAccountInfo: StripeDetails) => {
		return (
			<Row align={'middle'}>
				<Col span={14} xxl={12}>
					<Card className={styles.accountDetailsCard} bordered={false}>
						<Row>
							<Col span={3} xxl={2}>
								{stripeAccountInfo.bankName && (
									<BankFilled className={styles.bankIcon} />
								)}
								{!stripeAccountInfo.bankName && (
									<CreditCardOutlined className={styles.bankIcon} />
								)}
							</Col>
							<Col span={9} xxl={5}>
								<b>{stripeAccountInfo.bankName || stripeAccountInfo.brand}</b>
								<br />

								<p>
									{stripeAccountInfo.routingNumber && (
										<GoldFilled rotate={270} style={{ paddingRight: '2px' }} />
									)}
									{stripeAccountInfo.routingNumber ||
										`Debit ****${stripeAccountInfo.last4}`}
									{stripeAccountInfo.routingNumber && (
										<GoldFilled rotate={270} style={{ paddingRight: '2px' }} />
									)}
								</p>
							</Col>
							<Col span={12} xxl={6}>
								<Tag>{stripeAccountInfo.currency.toUpperCase()}</Tag>
								<CheckCircleOutlined className={styles.statusIcon} />
								<br />
								<p>
									{stripeAccountInfo.bankName && (
										<>&#8226; &#8226; &#8226; &#8226; &#8226; </>
									)}
									{stripeAccountInfo.bankName && stripeAccountInfo.last4}
								</p>
							</Col>
						</Row>
					</Card>
				</Col>
				<Col lg={{ offset: 3 }} sm={{ offset: 0 }} span={6}>
					<Button
						type="default"
						className={styles.paymentMethod + ' color-dark'}
						onClick={() => redirectToStripe(stripeAccountInfo.accountLink)}
					>
						{t('financePage.ChangePaymentMethod')}
					</Button>
				</Col>
			</Row>
		);
	};
	const redirectAction = () => {
		if (productDetails?.price == 0) {
			history.push(`/project/${projectId}/store-details`);
		} else {
			fetchAndRedirectToStripe();
		}
	};
	return (
		<>
			<div className={styles.financeDetailsContainer}>
				<div className="page-head">
					<div>
						<h2 className="h2">{t('financePage.Header')}</h2>
					</div>
				</div>

				<Tabs defaultActiveKey={!stripeAccountInfo ? '2' : '1'}>
					<TabPane tab={t('financePage.RevenueAnalytics')} key="1">
						<RevenueAnalytics />
					</TabPane>
					<TabPane tab={t('financePage.Payments')} key="2">
						<Layout>
							{stripeAccountInfo && (
								<Card className={styles.accountDetailsContainer}>
									{getAccountDetailsCard(stripeAccountInfo)}
								</Card>
							)}
							{!stripeAccountInfo && (
								<Layout className={styles.contentHolder}>
									<h2>
										<Trans
											i18nKey={
												productDetails?.price == 0
													? 'financePage.NoPaymentMethod.zeroprice'
													: 'financePage.NoPaymentMethod'
											}
										/>
									</h2>
									<Button
										type="primary"
										data-testid="stripeAccountLink"
										loading={fetchingAccountLink}
										onClick={redirectAction}
									>
										{productDetails?.price == 0
											? t('financePage.GoToStoreDetails')
											: t('common.Configure')}
									</Button>
								</Layout>
							)}
							<br />
							<Row>{stripeAccountInfo && <Payments />}</Row>
						</Layout>
					</TabPane>
					<TabPane tab={t('financePage.SalesDetails')} key="4">
						<SalesDetails />
					</TabPane>
				</Tabs>
			</div>
		</>
	);
}
